/**
 * authService — SSOT for client-side auth transitions.
 *
 * Login / signup / password-reset live in the Django + Next.js marketing
 * origin (served by `VITE_API_URL`). The authenticated SPA runs on its
 * own origin (Vite dev: :5173, prod: its own bucket). This module owns
 * the single "log out" transition that bridges the two — clearing local
 * state and redirecting to the correct login page on the backend origin.
 */

import { apiClient } from './apiClient';

const ALLAUTH_SESSION_PATH = '/_allauth/browser/v1/auth/session';
const LOGIN_PATH = '/accounts/login/';
const LOGGED_OUT_QUERY = '?logged_out=1';

/** Django origin that serves the allauth login template. Delegates
 *  to `apiOrigin()` so both helpers stay in sync; falls back to the
 *  current window.location.origin (same-origin reverse-proxy mode)
 *  which also happens to be what allauth expects. */
import { apiOrigin } from './apiUrls';
function authOrigin(): string {
    const envOrigin = apiOrigin();
    if (envOrigin) return envOrigin;
    if (typeof window !== 'undefined') return window.location.origin;
    return '';
}

/** Read a cookie value by name (browser only). */
function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const target = `${name}=`;
    const segs = document.cookie.split(';');
    for (const seg of segs) {
        const s = seg.trimStart();
        if (s.startsWith(target)) return decodeURIComponent(s.slice(target.length));
    }
    return null;
}

/** Best-effort CSRF bootstrap — allauth requires an `X-CSRFToken` header
 *  on unsafe methods. We fetch `/api/auth/csrf/` first, which sets the
 *  `csrftoken` cookie, then read it for the outgoing request. */
async function fetchCsrfToken(): Promise<string | null> {
    try {
        await apiClient.get('/api/auth/csrf/');
    } catch {
        // Cookie may already be present; fall through and try to read it.
    }
    return readCookie('csrftoken');
}

/**
 * Clear local auth state. Split out so callers can force-wipe state
 * without triggering a network request (e.g. on 401 response).
 */
export function clearLocalSession(): void {
    try {
        localStorage.clear();
        sessionStorage.clear();
    } catch {
        // Private browsing mode or SSR — swallow; redirect will still fire.
    }
}

/**
 * Best-effort logout. Always clears local state and redirects to login,
 * even if the server call fails (stale session, offline, etc.). This
 * matches big-corp SaaS behaviour where "Log out" never leaves the user
 * stuck on an authenticated screen.
 */
export async function logout(options: { redirect?: boolean } = {}): Promise<void> {
    const { redirect = true } = options;
    try {
        // allauth protects unsafe methods with CSRF; attach the token
        // explicitly so the request doesn't 403 on CSRF failure.
        const csrf = await fetchCsrfToken();
        await apiClient.delete(
            ALLAUTH_SESSION_PATH,
            csrf ? { headers: { 'X-CSRFToken': csrf } } : undefined,
        );
    } catch (err) {
        // Network failure or already logged out — fall through.
        console.warn('[authService] logout request failed; clearing local state anyway', err);
    }
    clearLocalSession();
    if (redirect && typeof window !== 'undefined') {
        // Login template is served by Django (backend origin), not Vite,
        // so prefix the redirect with VITE_API_URL to cross the boundary.
        window.location.href = `${authOrigin()}${LOGIN_PATH}${LOGGED_OUT_QUERY}`;
    }
}
